<template>
  <div class="documentation-step">
    <!-- Sección 1: Selección de tipo de documento -->
    <div class="box has-background-dark has-text-light">
      <div class="level is-mobile">
        <div class="level-left">
          <div class="level-item">
            <span class="icon is-large has-text-primary">
              <i class="fa fa-id-card fa-2x"></i>
            </span>
          </div>
          <div class="level-item">
            <div>
              <h3 class="title is-4 has-text-light">
                {{ $t("identity documentation") }}
              </h3>
              <p class="subtitle is-6 has-text-grey-light">
                {{ $t("select document type and upload photos") }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Selector de tipo de documento -->
      <div class="field">
        <label class="label has-text-light">{{ $t("document type") }} *</label>
        <div class="buttons is-centered">
          <button
            type="button"
            class="button is-medium"
            :class="
              formData.tipoDocumento === 'pasaporte'
                ? 'is-primary is-selected'
                : 'is-primary is-outlined'
            "
            @click="$emit('update:tipo-documento', 'pasaporte')"
          >
            <span class="icon">
              <i class="fa fa-id-card"></i>
            </span>
            <span>{{ $t("passport") }}</span>
          </button>

          <button
            type="button"
            class="button is-medium"
            :class="
              formData.tipoDocumento === 'dpi'
                ? 'is-primary is-selected'
                : 'is-primary is-outlined'
            "
            @click="$emit('update:tipo-documento', 'dpi')"
          >
            <span class="icon">
              <i class="fa fa-id-card"></i>
            </span>
            <span>{{ $t("dpi") }}</span>
          </button>

          <button
            type="button"
            class="button is-medium"
            :class="
              formData.tipoDocumento === 'licencia'
                ? 'is-primary is-selected'
                : 'is-primary is-outlined'
            "
            @click="$emit('update:tipo-documento', 'licencia')"
          >
            <span class="icon">
              <i class="fa fa-id-badge"></i>
            </span>
            <span>{{ $t("licence") }}</span>
          </button>

          <button
            type="button"
            class="button is-medium"
            :class="
              formData.tipoDocumento === 'partida'
                ? 'is-primary is-selected'
                : 'is-primary is-outlined'
            "
            @click="$emit('update:tipo-documento', 'partida')"
          >
            <span class="icon">
              <i class="fa fa-certificate"></i>
            </span>
            <span>{{ $t("birth certificate") }}</span>
          </button>
        </div>
        <p v-if="errors.tipoDocumento" class="help is-danger">
          {{ errors.tipoDocumento }}
        </p>
      </div>

      <!-- Número de documento -->
      <div class="field" v-if="formData.tipoDocumento">
        <label class="label has-text-light"
          >{{ $t("document number") }} *</label
        >
        <div class="control has-icons-left">
          <input
            class="input is-dark"
            :class="{ 'is-danger': errors.numeroDocumento }"
            type="text"
            :value="formData.numeroDocumento"
            @input="$emit('update:numero-documento', $event.target.value)"
            @blur="$emit('validate-field', 'numeroDocumento')"
            :placeholder="getDocumentPlaceholder()"
            required
          />
          <span class="icon is-small is-left has-text-grey-light">
            <i class="fa fa-hashtag"></i>
          </span>
        </div>
        <p v-if="errors.numeroDocumento" class="help is-danger">
          {{ errors.numeroDocumento }}
        </p>
      </div>
    </div>

    <!-- Sección 2: Fotos del documento -->
    <div
      class="box has-background-grey-dark has-text-light"
      v-if="formData.tipoDocumento"
    >
      <div class="level is-mobile">
        <div class="level-left">
          <div class="level-item">
            <span class="icon is-large has-text-info">
              <i class="fa fa-camera fa-2x"></i>
            </span>
          </div>
          <div class="level-item">
            <div>
              <h3 class="title is-4 has-text-light">
                {{ $t("document photos") }}
              </h3>
              <p class="subtitle is-6 has-text-grey-light">
                {{ $t("take clear photos of both sides") }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <div class="card has-background-black-ter">
            <div class="card-header has-background-grey-darker">
              <p class="card-header-title has-text-light">
                {{ $t("front document") }} *
              </p>
            </div>
            <div class="card-content">
              <CameraCapture
                field-name="documentoFrontal"
                :image-preview="previews.documentoFrontal"
                @photo-captured="handleCameraCapture"
                @file-uploaded="handleFileUpload"
              />
            </div>
          </div>
        </div>

        <div class="column">
          <div class="card has-background-black-ter">
            <div class="card-header has-background-grey-darker">
              <p class="card-header-title has-text-light">
                {{ $t("back document") }} *
              </p>
            </div>
            <div class="card-content">
              <CameraCapture
                field-name="documentoTrasero"
                :image-preview="previews.documentoTrasero"
                @photo-captured="handleCameraCapture"
                @file-uploaded="handleFileUpload"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección 3: Documentación adicional -->
    <div class="box has-background-grey-dark has-text-light">
      <div class="level is-mobile">
        <div class="level-left">
          <div class="level-item">
            <span class="icon is-large has-text-warning">
              <i class="fa fa-file-text fa-2x"></i>
            </span>
          </div>
          <div class="level-item">
            <div>
              <h3 class="title is-4 has-text-light">
                {{ $t("additional documentation") }}
              </h3>
              <p class="subtitle is-6 has-text-grey-light">
                {{ $t("upload proof of address and selfie") }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <div class="card has-background-black-ter">
            <div class="card-header has-background-grey-darker">
              <p class="card-header-title has-text-light">
                {{ $t("proof of address") }} *
              </p>
              <!-- <p class="card-header-subtitle has-text-grey-light">
                {{ $t("utility bill or bank statement") }}
              </p> -->
            </div>
            <div class="card-content">
              <CameraCapture
                field-name="comprobanteDomicilio"
                :image-preview="previews.comprobanteDomicilio"
                @photo-captured="handleCameraCapture"
                @file-uploaded="handleFileUpload"
              />
            </div>
          </div>
        </div>

        <div class="column">
          <div class="card has-background-black-ter">
            <div class="card-header has-background-grey-darker">
              <p class="card-header-title has-text-light">
                {{ $t("selfie verification") }} *
              </p>
              <!-- <p class="card-header-subtitle has-text-grey-light">
                {{ $t("take a clear photo of yourself") }}
              </p> -->
            </div>
            <div class="card-content">
              <SelfieCapture
                :selfie-preview="selfiePreview"
                @handle-file-upload="
                  (fieldName, event) =>
                    $emit('handle-file-upload', fieldName, event)
                "
                @selfie-captured="(data) => $emit('selfie-captured', data)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Consejos de ayuda -->
    <div class="notification is-info is-light">
      <div class="level is-mobile">
        <div class="level-left">
          <div class="level-item">
            <span class="icon has-text-info">
              <i class="fa fa-lightbulb-o"></i>
            </span>
          </div>
          <div class="level-item">
            <strong>{{ $t("photo tips") }}</strong>
          </div>
        </div>
      </div>
      <div class="content">
        <ul>
          <li>{{ $t("tip 1: good lighting") }}</li>
          <li>{{ $t("tip 2: clear text") }}</li>
          <li>{{ $t("tip 3: no glare") }}</li>
          <li>{{ $t("tip 4: complete document") }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import SelfieCapture from "./SelfieCapture.vue";
import CameraCapture from "./CameraCapture.vue";

export default {
  name: "DocumentationStep",
  components: {
    SelfieCapture,
    CameraCapture,
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
    previews: {
      type: Object,
      required: true,
    },
    selfiePreview: {
      type: String,
      default: null,
    },
  },
  emits: [
    "update:tipo-documento",
    "update:numero-documento",
    "handle-file-upload",
    "selfie-captured",
    "validate-field",
  ],
  methods: {
    getDocumentPlaceholder() {
      switch (this.formData.tipoDocumento) {
        case "pasaporte":
          return "Ej: A1234567";
        case "dpi":
          return "Ej: 1234567890123";
        case "licencia":
          return "Ej: A123456789";
        case "partida":
          return "Ej: 123456";
        default:
          return "Número del documento";
      }
    },
    handleCameraCapture(data) {
      // Crear un evento fake para mantener compatibilidad
      const fakeEvent = {
        target: {
          files: data.file ? [data.file] : [],
        },
      };
      this.$emit("handle-file-upload", data.fieldName, fakeEvent);
    },
    handleFileUpload(data) {
      // Crear un evento fake para mantener compatibilidad
      const fakeEvent = {
        target: {
          files: data.file ? [data.file] : [],
        },
      };
      this.$emit("handle-file-upload", data.fieldName, fakeEvent);
    },
  },
};
</script>

<style scoped>
/* Mejoras adicionales para el diseño con Bulma */
.documentation-step {
  max-width: 100%;
}

/* Espaciado entre secciones */
.box {
  margin-bottom: 1.5rem;
}

/* Mejorar el espaciado de los botones de tipo de documento */
.buttons.is-centered {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.buttons.is-centered .button {
  margin: 0.25rem;
}

/* Estados personalizados para botones seleccionados */
.button.is-selected {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(50, 115, 220, 0.3);
}

/* Animaciones suaves */
.button {
  transition: all 0.3s ease;
}

.card {
  transition: transform 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
}

/* Responsive para móviles */
@media (max-width: 768px) {
  .level {
    flex-direction: column;
    text-align: center;
  }

  .level-left {
    justify-content: center;
    margin-bottom: 1rem;
  }

  .buttons.is-centered {
    justify-content: center;
  }

  .buttons.is-centered .button {
    flex: 1;
    min-width: 120px;
  }
}

/* Mejorar contraste para inputs oscuros */
.input.is-dark::placeholder {
  color: #b5b5b5;
}

.input.is-dark:focus {
  border-color: #3273dc;
  box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
}
</style>
